import { useState, useEffect } from 'react';

/**
 * Debounce window (ms). The native `resize` event fires on every pixel of a
 * drag-resize; committing each one re-renders every consumer 60×/sec. Commit
 * only once resizing settles instead.
 */
const RESIZE_SETTLE_MS = 100;

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  const isMobile = width <= 768;
  const isMonitor = width > 1536;
  return {
    width,
    height,
    isMobile,
    isMonitor,
  };
}

function areDimensionsEqual(a, b) {
  return (
    a.width === b.width &&
    a.height === b.height &&
    a.isMobile === b.isMobile &&
    a.isMonitor === b.isMonitor
  );
}

export default function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions);

  useEffect(() => {
    let timeoutId = null;

    const commit = () => {
      const next = getWindowDimensions();
      // Change-guard: keep the previous reference when nothing changed so
      // consumers do not re-render on a no-op resize event.
      setWindowDimensions((prev) =>
        areDimensionsEqual(prev, next) ? prev : next
      );
    };

    const handleResize = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(commit, RESIZE_SETTLE_MS);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return windowDimensions;
}
